import RightArrowIcon from "@/assets/colorable/right_arrow.svg?react";
import DynamicLocationField from "@/components/Common/forms/DynamicLocationField";
import InputField from "@/components/Common/forms/InputField";
import OptionalChip from "@/components/Common/forms/OptionalChip";
import CountrySelect from "@/components/ui/CountrySelect";
import useCountries from "@/hooks/useCountries";
import clsxm from "@/lib/clsxm";
import type {TSetter} from "@/store/createStore";
import {debounce, kebabCase} from "lodash";
import {useState} from "react";
import {useTranslation} from "react-i18next";

type AddressFormProps<T> = {
  cityKey: keyof T;
  className?: string;
  countryKey: keyof T;
  errors?: Record<string, string>;
  inputClassName?: string;
  isDisabled?: boolean;
  labelClassName?: string;
  onDynamicAddressBlur?: () => void;
  onDynamicAddressFocus?: () => void;
  optional?: boolean;
  placeDescriptionKey: keyof T;
  placeIdKey: keyof T;
  postalClassName?: string;
  postalKey: keyof T;
  storeForm: T;
  storeFormKey?: keyof T;
  storeSetter?: (key: any, value: T[keyof T]) => void;
  storeSetterFn?: TSetter<any>;
  streetKey: keyof T;
  title?: string;
  titleClassName?: string;
  validateForm?: () => boolean;
};

const AddressForm = ({
  cityKey,
  className,
  countryKey,
  errors,
  inputClassName,
  isDisabled,
  labelClassName,
  onDynamicAddressBlur,
  onDynamicAddressFocus,
  optional,
  placeDescriptionKey,
  placeIdKey,
  postalClassName,
  postalKey,
  storeForm,
  storeFormKey,
  storeSetter,
  storeSetterFn,
  streetKey,
  title,
  titleClassName,
  validateForm,
}: AddressFormProps<Record<string, any>>) => {
  const {t} = useTranslation(["common", "company_profile"]);
  const {getCountryCodeByEnLabel, getCountryEnLabelByTranslatedLabel, getCountryEnLabelByCode, allCountries} =
    useCountries();
  const [areManualAddressFieldsShown, setManualAddressFieldsShown] = useState(
    !storeForm[placeDescriptionKey] && !!storeForm[cityKey],
  );
  const [isSwitchButtonShown, setSwitchButtonShown] = useState(areManualAddressFieldsShown);

  const setStoreValue = (key: string, value?: string | null) => {
    if (!!storeSetterFn) {
      storeSetterFn((state: Record<string, any>) => {
        if (!!storeFormKey) state[storeFormKey][key] = value;
        else state[key] = value;
      });
      return;
    }

    storeSetter?.(key, value);
  };
  const onChangeManualField = (key: string, value: string) => {
    setStoreValue(key, value);

    setStoreValue(placeIdKey, null);
    setStoreValue(placeDescriptionKey, null);
  };

  if (!allCountries) {
    return null;
  }

  return (
    <div className={clsxm("flex flex-col gap-3", className)}>
      <div className="flex justify-between">
        <label className={clsxm("text-neutral-grey-dark flex items-center gap-2 text-sm font-medium", titleClassName)}>
          {optional && <OptionalChip />}
          {areManualAddressFieldsShown ? t("Country") : (title ?? t("companiesAddress"))}
        </label>
        {isSwitchButtonShown && (
          <button
            data-testid="address-toggle-manual-button"
            className="text-primary-dark-solid flex items-center gap-2"
            onClick={() => setManualAddressFieldsShown(!areManualAddressFieldsShown)}>
            <span className="items-center text-xs">
              {t(`company_profile:enable_${areManualAddressFieldsShown ? "autocomplete" : "manual"}_address`)}
            </span>
            <RightArrowIcon className="size-3" />
          </button>
        )}
      </div>
      {areManualAddressFieldsShown ? (
        <div className="flex flex-col gap-2">
          <CountrySelect
            value={getCountryCodeByEnLabel(storeForm[countryKey])}
            disabled={isDisabled}
            onChange={country => {
              onChangeManualField(countryKey, getCountryEnLabelByCode(country));
              if (storeForm[streetKey]) validateForm?.();
            }}
            hidePhoneNumber
            showCountryName
            buttonClassName={clsxm("w-full border rounded-md justify-start gap-2", inputClassName)}
            chevronClassName="ml-auto mr-2"
          />
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <InputField
              customFieldId={`registration-${kebabCase(streetKey)}-field`}
              dataTestId={`registration-${kebabCase(streetKey)}-field`}
              placeholder={t("companiesAddressPlaceholder")}
              onChange={value => onChangeManualField(streetKey, value)}
              className={clsxm(
                "border-primary-light-solid",
                inputClassName,
                errors && errors[streetKey] && "border-danger-medium-red focus:border-danger-dark-red",
              )}
              error={errors ? errors[streetKey] : null}
              value={storeForm[streetKey] ?? ""}
              isDisabled={isDisabled}
              label={t("companiesAddressPlaceholder")}
              labelClassName={labelClassName}
              onBlur={validateForm}
            />
            <InputField
              customFieldId={`registration-${kebabCase(cityKey)}-field`}
              dataTestId={`registration-${kebabCase(cityKey)}-field`}
              placeholder={t("city")}
              onChange={value => onChangeManualField(cityKey, value)}
              className={clsxm(
                "border-primary-light-solid",
                inputClassName,
                errors && errors[cityKey] && "border-danger-medium-red focus:border-danger-dark-red",
              )}
              error={errors ? errors[cityKey] : null}
              value={storeForm[cityKey] ?? ""}
              label={t("city")}
              labelClassName={labelClassName}
              isDisabled={isDisabled}
              onBlur={validateForm}
            />
          </div>
          <InputField
            customFieldId={`registration-${kebabCase(postalKey)}-field`}
            dataTestId={`registration-${kebabCase(postalKey)}-field`}
            placeholder={t("postal_code")}
            onChange={value => onChangeManualField(postalKey, value)}
            className={clsxm(
              "border-primary-light-solid sm:w-1/2",
              inputClassName,
              postalClassName,
              errors && errors[postalKey] && "border-danger-medium-red focus:border-danger-dark-red",
            )}
            error={errors ? errors[postalKey] : null}
            value={storeForm[postalKey] ?? ""}
            label={t("postal_code")}
            labelClassName={labelClassName}
            isDisabled={isDisabled}
            onBlur={validateForm}
          />
        </div>
      ) : (
        <div className="w-full" data-testid="registration-address-autocomplete">
          <DynamicLocationField
            disabled={isDisabled}
            labelClassName={clsxm("text-neutral-grey-dark flex min-h-5 flex-row gap-2 font-medium", labelClassName)}
            inputClassName={clsxm(
              "border-primary-light-solid text-neutral-black placeholder:text-neutral-grey-light focus:border-primary-medium-solid disabled:bg-neutral-white-dark disabled:text-neutral-grey-dark flex h-14 w-full rounded-md border bg-white p-4 outline-hidden transition-all duration-150 disabled:cursor-not-allowed",
              inputClassName,
              errors &&
                (errors[cityKey] || errors[streetKey] || errors[postalKey]) &&
                "border-danger-medium-red focus:border-danger-dark-red",
            )}
            defaultValue={storeForm[placeDescriptionKey] ?? ""}
            onBlur={() => {
              validateForm?.();
              onDynamicAddressBlur?.();
            }}
            onChange={debounce(() => {
              setSwitchButtonShown(true);
            }, 500)}
            onFocus={onDynamicAddressFocus}
            onPick={place => {
              setStoreValue(
                streetKey,
                !place.street ? null : `${place.street}${place.streetNumber ? ` ${place.streetNumber}` : ""}`,
              );

              setStoreValue(postalKey, place.postalCode);
              setStoreValue(cityKey, place.city);
              setStoreValue(countryKey, getCountryEnLabelByTranslatedLabel(place.country));
              setStoreValue(placeIdKey, place.placeId);

              if (place.establishment) {
                setStoreValue(
                  placeDescriptionKey,
                  `${place.establishment}${place.description ? ", " : ""}${place.description}`,
                );
              } else {
                setStoreValue(placeDescriptionKey, place.description);
              }

              validateForm?.();
            }}
          />
          {errors && (errors[cityKey] || errors[streetKey] || errors[postalKey]) && (
            <ul className="text-danger-medium-red mt-0.5 p-0 text-xs">
              {errors[cityKey] && <li>{t(errors[cityKey] as "company_profile:missing_address_city_error")}</li>}
              {errors[streetKey] && <li>{t(errors[streetKey] as "company_profile:missing_address_street_error")}</li>}
              {errors[postalKey] && <li>{t(errors[postalKey] as "company_profile:missing_address_postal_error")}</li>}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default AddressForm;
